import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';

export const TrainingArticles = () => {
  const { language } = useLanguage();

  const articles = language === 'fr' ? [
    {
      title: 'Comparatif des meilleures formations IA pour avocats',
      excerpt: 'Analyse détaillée des programmes disponibles sur le marché et critères de choix.',
      slug: '/articles/comparatif-formations-ia-avocats',
      tag: 'Analyse Stratégique',
    },
    {
      title: 'Webinaire ou bootcamp IA : quel format choisir ?',
      excerpt: 'Avantages et inconvénients de chaque format pour maximiser la montée en compétences.',
      slug: '/articles/webinaire-vs-bootcamp-formation-ia',
      tag: 'Analyse Stratégique',
    },
    {
      title: 'Formation IA pour juristes d\'entreprise',
      excerpt: 'Spécificités et enjeux de la formation IA pour les directions juridiques.',
      slug: '/articles/formation-ia-juristes-entreprise',
      tag: 'Analyse Stratégique',
    },
    {
      title: 'Financer sa formation IA juridique',
      excerpt: 'OPCO, FIF-PL, crédit d\'impôt : toutes les options de financement décryptées.',
      slug: '/articles/financement-formation-ia-juridique',
      tag: 'Do It Yourself',
    },
  ] : [
    {
      title: 'Best AI Training for Lawyers — Comparison',
      excerpt: 'Detailed analysis of programs available on the market and selection criteria.',
      slug: '/articles/best-ai-training-lawyers-comparison',
      tag: 'Strategic Analysis',
    },
    {
      title: 'Webinar vs. Bootcamp: Which Format to Choose?',
      excerpt: 'Pros and cons of each format to maximize skill development.',
      slug: '/articles/webinar-vs-bootcamp-ai-training',
      tag: 'Strategic Analysis',
    },
    {
      title: 'AI Training for In-House Counsel',
      excerpt: 'Specific challenges and opportunities for legal department AI training.',
      slug: '/articles/ai-training-in-house-counsel',
      tag: 'Strategic Analysis',
    },
    {
      title: 'Funding Your Legal AI Training',
      excerpt: 'All funding options decoded: OPCO, FIF-PL, tax credits, and more.',
      slug: '/articles/legal-ai-training-funding',
      tag: 'Do It Yourself',
    },
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <BookOpen className="w-7 h-7 text-primary" />
          <h2 className="text-3xl font-bold text-foreground">
            {language === 'fr' ? 'Nos articles sur la formation IA' : 'Our Articles on AI Training'}
          </h2>
        </div>
        <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          {language === 'fr'
            ? 'Approfondissez le sujet avec nos analyses et guides dédiés à la formation IA juridique.'
            : 'Dive deeper with our analysis and guides dedicated to legal AI training.'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {articles.map((article, index) => (
            <Card key={index} className="bg-white border-border hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <Badge variant="outline" className="w-fit text-xs mb-2">{article.tag}</Badge>
                <CardTitle className="text-lg text-foreground">
                  <Link to={article.slug} className="hover:text-primary transition-colors">
                    {article.title}
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{article.excerpt}</p>
                <Link to={article.slug} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1">
                  {language === 'fr' ? 'Lire l\'article' : 'Read article'}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center">
          <Button asChild variant="outline" size="lg">
            <Link to={language === 'fr' ? '/actualites' : '/blog'} className="flex items-center gap-2">
              {language === 'fr' ? 'Voir tous nos articles' : 'View All Articles'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
